<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="main">
    <div class="left-menu-container">
      <Menus
        :menu="menu"
        icon="https://dsmlib.logistics.systems/Icons/icons8-edit-row-64.png"
        @menuClicked="menuClicked"
      />
    </div>
    <div class="right-tablebox-container">
      <TableBox
        :tableData="prTableDataFiltered"
        :selectedRow.sync="prTableSelectedRows"
        :loading="prTableLoading"
        title="Purchase Requisition"
        :dataType="prTableDataType"
      />
    </div>
    <PopupEntry
      class="popup-entry"
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popupTitle"
      :viewMode="viewMode"
      :updatedBy="lastUpdatedInfo"
      :requiredError="popupEntryRequiredError"
      :successMessages="popupEntrySuccessMessages"
      :errorMessages="popupEntryErrorMessages"
      @save="popupEntrySaveClicked"
    >
      <div class="flex-container px-2">
        <DateTimePicker
          placeholder="Date/Time"
          :value.sync="prDateValue"
          :errorMessage.sync="prDateValueError"
          :showTime="false"
          :isRequired="true"
          :isDisabled="viewMode"
          ref="PRDate"
        />
        <Input
          placeholder="Requested By"
          style="width: 280px; margin-left: auto"
          :isDisabled="true"
          :inputValue="requestedByValue"
        />
      </div>
      <div class="flex-container h-100 w-100 pb-2">
        <div class="w-100 d-flex flex-column">
          <div class="tab-component-container w-100 h-100">
            <Tab
              class="tab-component"
              :tabs="tabItems"
              :tabIndex.sync="selectedTabIndex"
              :preventTabSwitch="true"
              @tabClicked="tabClick"
            >
              <div class="tab-container" slot="General Info">
                <div style="margin-top: -8px; padding-top: 8px">
                  <RadioButtonSet
                    title="PO/WO"
                    :buttons="pesananButtons"
                    labelKey="label"
                    :isHorizontal="true"
                    :selected.sync="pesananValue"
                    :errorMessage.sync="pesananValueError"
                    :isRequired="true"
                    :isDisabled="viewMode"
                    ref="pesanan"
                  />
                  <RadioButtonSet
                    title="Expense"
                    :buttons="expenseTypeButtons"
                    labelKey="label"
                    :isHorizontal="true"
                    :selected.sync="expenseTypeValue"
                    :errorMessage.sync="expenseTypeValueError"
                    :isRequired="true"
                    :isDisabled="viewMode"
                    ref="expenseType"
                  />
                  <div class="flex-container flex-column my-2">
                    <Input
                      placeholder="Estimated Cost"
                      :inputValue.sync="estimatedCostValue"
                      validator="currency"
                      :isDisabled="viewMode"
                    />
                    <Input
                      placeholder="Period Usage"
                      :inputValue.sync="periodUsageValue"
                      validator="text,20"
                      :isDisabled="viewMode"
                    />
                  </div>
                </div>
              </div>
              <div class="tab-container" slot="Description">
                <div class="flex-container flex-column">
                  <div style="display: flex; flex-direction: column; width: 100%">
                    Title:
                    <textarea
                      placeholder="Title"
                      name=""
                      id=""
                      rows="4"
                      v-model="titleValue"
                      :disabled="viewMode"
                    ></textarea>
                  </div>
                  <div style="display: flex; flex-direction: column; width: 100%">
                    Scope:
                    <textarea
                      placeholder="Scope"
                      name=""
                      id=""
                      rows="4"
                      v-model="scopeValue"
                      :disabled="viewMode"
                    ></textarea>
                  </div>
                  <div style="display: flex; flex-direction: column; width: 100%">
                    Justification:
                    <textarea
                      placeholder="Justification"
                      name=""
                      id=""
                      rows="4"
                      v-model="justificationValue"
                      :disabled="viewMode"
                    ></textarea>
                  </div>
                </div>
              </div>
              <div class="tab-container table-tab-container" :slot="quotationsTitle">
                <div class="table-container">
                  <div class="flex-container justify-content-end py-1" v-if="!viewMode">
                    <Button style="display: flex; align-items: center" @click="prImageAdd"
                      ><img src="https://dsmlib.logistics.systems/Icons/Attachment.png" width="16" alt="" /> Add
                      Attachment</Button
                    >
                    <Button :disabled="prImagesTableSelectedRows.length < 1" @click="prImageDelete">Delete</Button>
                    <input type="file" ref="fileInput" style="display: none" @change="onFileChange" />
                  </div>
                  <TableBox
                    style="min-height: 0"
                    title="Quotations"
                    :tableData="prImagesTableDataFiltered"
                    :selectedRow.sync="prImagesTableSelectedRows"
                    :hideColumns="['PRImage', 'seq', 'action']"
                    :additionalButtons="[{ text: 'Download', index: 0, width: 100 }]"
                    @buttonClicked="prImageDownload"
                    :dataType="prImagesDataType"
                  />
                </div>
              </div>
              <div class="tab-container table-tab-container" :slot="itemsTitle">
                <div class="table-container">
                  <div class="flex-container justify-content-end py-1" v-if="!viewMode">
                    <Button @click="prItemAddClick" :disabled="selectedPrItem">Add</Button>
                    <Button @click="prItemDelete" :disabled="selectedPrItem || prItemsTableSelectedRows.length < 1"
                      >Delete</Button
                    >
                  </div>
                  <TableBox
                    style="min-height: 0"
                    title="Items"
                    :tableData="prItemsTableDataFiltered"
                    :hideColumns="['SeqNo', 'action']"
                    :selectedRow.sync="prItemsTableSelectedRows"
                    :additionalButtons="tableAdditionalButton"
                    @buttonClicked="prItemTableButtonClick"
                    :dataType="prItemsDataType"
                  />
                </div>
                <div class="pt-1" v-if="selectedPrItem">
                  <div class="flex-container justify-content-end mb-3">
                    <button @click="prItemSave" v-if="!viewMode">Save</button>
                    <button @click="prItemCancel">Cancel</button>
                  </div>
                  <InputQuery
                    placeholder="Supplier"
                    :isRequired="true"
                    :selectedObject.sync="supplierValue"
                    :errorMessage.sync="supplierValueError"
                    :items="supplierList"
                    itemKey="label"
                    :isDisabled="viewMode"
                    :tableBoxHideColumns="['label', 'cocode']"
                    :tableBoxDataType="supplierDataType"
                  />
                  <InputQuery
                    placeholder="Product"
                    :isRequired="true"
                    :selectedObject.sync="productValue"
                    :errorMessage.sync="productValueError"
                    :items="whProductList"
                    @selected="whProductValueChanged"
                    itemKey="label"
                    :tableBoxHideColumns="[
                      'Product ID',
                      'Sub',
                      'Metric',
                      'DefaultUnitDescription',
                      'subPackCode1',
                      'Sub1UnitDescription',
                      'subPackCode2',
                      'Sub2UnitDescription',
                      'subQtyUnit2',
                      'subQtyUnit3',
                      'QtyOnHand',
                      'label',
                      'ItemTypeKey',
                      'ARTaxCode',
                      'ARTaxDesc',
                      'ARTaxRate',
                      'ARTaxType',
                      'APTaxCode',
                      'APTaxDesc',
                      'APTaxRate',
                      'APTaxType',
                    ]"
                    :isDisabled="viewMode"
                    :tableBoxDataType="whProductListDataType"
                  />
                  <Input
                    placeholder="Quantity"
                    :isRequired="true"
                    :inputValue.sync="quantityValue"
                    :errorMessage.sync="quantityValueError"
                    validator="positiveDecimal"
                    :isDisabled="viewMode"
                  />
                  <Input
                    placeholder="Unit Price"
                    :allowFreeText="true"
                    :isRequired="true"
                    :inputValue.sync="unitPriceValue"
                    :errorMessage.sync="unitPriceValueError"
                    itemKey="Price"
                    validator="currency"
                    :isDisabled="viewMode"
                  />
                  <div class="mt-1">Amount Total: {{ $currencyFormat(amountTotal) }}</div>
                  <BoxContainer title="Taxes" v-if="taxInfo">
                    <div>Tax Code: {{ taxInfo.APTaxCode }} - {{ taxInfo.APTaxDesc }}</div>
                    <div>Tax Rate: {{ taxInfo.APTaxRate }} %</div>
                    <div>Tax Amount: {{ $currencyFormat(taxInfo.TaxAmount) }}</div>
                  </BoxContainer>
                  <div class="mt-2" v-if="taxInfo">Net Inclusive Tax: {{ $currencyFormat(netAmount) }}</div>
                </div>
              </div>
              <div class="tab-container" slot="Person In Charge">
                <div class="flex-container flex-column" style="max-width: 1000px">
                  <div class="flex-container align-items-center">
                    <div style="width: 560px">Requested: </div>
                    <Dropdown
                      placeholder="Requested"
                      :items="employeeList"
                      itemKey="Employee Name"
                      :allowFreeText="true"
                      :search.sync="pic1Value"
                      :isDisabled="viewMode"
                    />
                    <Input placeholder="Email" :inputValue.sync="email1Value" :isDisabled="viewMode" />
                  </div>
                  <div class="flex-container align-items-center">
                    <div style="width: 560px">Branch Technical: </div>
                    <Dropdown
                      placeholder="Branch Technical"
                      :items="employeeList"
                      itemKey="Employee Name"
                      :allowFreeText="true"
                      :search.sync="pic2Value"
                      :isDisabled="viewMode"
                    />
                    <Input placeholder="Email" :inputValue.sync="email2Value" :isDisabled="viewMode" />
                  </div>
                  <div class="flex-container align-items-center">
                    <div style="width: 560px">Branch HR and Admin: </div>
                    <Dropdown
                      placeholder="Branch HR and Admin"
                      :items="employeeList"
                      itemKey="Employee Name"
                      :allowFreeText="true"
                      :search.sync="pic3Value"
                      :isDisabled="viewMode"
                    />
                    <Input placeholder="Email" :inputValue.sync="email3Value" :isDisabled="viewMode" />
                  </div>
                  <div class="flex-container align-items-center">
                    <div style="width: 560px">Branch Manager: </div>
                    <Dropdown
                      placeholder="Branch Manager"
                      :items="employeeList"
                      itemKey="Employee Name"
                      :allowFreeText="true"
                      :search.sync="pic4Value"
                      :isDisabled="viewMode"
                    />
                    <Input placeholder="Email" :inputValue.sync="email4Value" :isDisabled="viewMode" />
                  </div>
                  <div class="flex-container align-items-center">
                    <div style="width: 560px">HQ Technical/HR and Admin: </div>
                    <Dropdown
                      placeholder="HQ Technical/HR and Admin"
                      :items="employeeList"
                      itemKey="Employee Name"
                      :allowFreeText="true"
                      :search.sync="pic5Value"
                      :isDisabled="viewMode"
                    />
                    <Input placeholder="Email" :inputValue.sync="email5Value" :isDisabled="viewMode" />
                  </div>
                  <div class="flex-container align-items-center">
                    <div style="width: 560px">Chairman / Managing Director: </div>
                    <Dropdown
                      placeholder="Chairman / Managing Director"
                      :items="employeeList"
                      itemKey="Employee Name"
                      :allowFreeText="true"
                      :search.sync="pic6Value"
                      :isDisabled="viewMode"
                    />
                    <Input placeholder="Email" :inputValue.sync="email6Value" :isDisabled="viewMode" />
                  </div>
                </div>
              </div>
            </Tab>
          </div>
          <BoxContainer title="Status" v-if="selectedPR">
            <div
              :style="{
                display: 'grid',
                gridTemplateColumns: !viewMode ? 'repeat(2, max-content)' : 'repeat(3, max-content)',
                gap: '4px',
                alignItems: 'center',
              }"
            >
              <div>Checked By Branch</div>
              <div style="margin-right: 16px">: {{ selectedPR.BranchCheck == '1' ? 'Checked ✅' : 'Pending' }}</div>
              <Button
                v-if="viewMode"
                :style="{
                  visibility: !prStatusUpdateSAS.branchCheck && selectedPR.BranchCheck != '1' ? 'visible' : 'hidden',
                }"
                @click="approvalClick('BRC')"
                >Check By Branch</Button
              >

              <div>Approved By Branch</div>
              <div style="margin-right: 16px">: {{ selectedPR.BranchApproval == '1' ? 'Approved ✅' : 'Pending' }}</div>
              <Button
                v-if="viewMode"
                :style="{
                  visibility:
                    !prStatusUpdateSAS.branchApprove && selectedPR.BranchApproval != '1' ? 'visible' : 'hidden',
                }"
                @click="approvalClick('BR')"
                >Approve By Branch
              </Button>

              <div>Approved By HQ</div>
              <div style="margin-right: 16px">: {{ selectedPR.HQApproval == '1' ? 'Approved ✅' : 'Pending' }}</div>
              <Button
                v-if="viewMode"
                :style="{
                  visibility: !prStatusUpdateSAS.hqApprove && selectedPR.HQApproval != '1' ? 'visible' : 'hidden',
                }"
                @click="approvalClick('HQ')"
                >Approve By HQ</Button
              >
            </div>
          </BoxContainer>
        </div>
        <TableBox
          title="Event List"
          style="flex: 0 0 520px; min-width: 0; max-width: 32dvw"
          :tableData="prEventTableDataFiltered"
          :hideBottomBlock="true"
          :columnWidth="[40, 220, 80, 160]"
          :dataType="prEventDataType"
        />
      </div>
    </PopupEntry>

    <PopupV2
      :show="showDeletePopup"
      title="Delete Purchase Requisition"
      @buttonClicked="deletePopupButtonClicked"
      :buttons="[
        { text: 'Delete', color: 'red', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ]"
    >
      <div
        >Are you sure you want to delete Purchase Requisition
        {{ prTableSelectedRows[0] ? prTableSelectedRows[0]['PO #'] : '' }}?</div
      >

      <Input placeholder="Reason" :inputValue.sync="deleteRemarks" :errorMessage.sync="deleteRemarksError" />
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    menu() {
      const menuEnabled = !this.prTableLoading;

      let menuItems = [
        {
          title: 'New Purchase Requisition',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'New',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuNew',
        },
        {
          title: 'Edit Purchase Requisition',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'Edit',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuEdit',
        },
        {
          title: 'Delete Purchase Requisition',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'Delete',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuDelete',
        },
        {
          title: 'View Purchase Requisition',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'View',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuFind',
        },
        {
          title: 'Print Purchase Requisition',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          key: 'Print',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuPrint',
        },
      ];

      menuItems = menuItems.map((item) => {
        const { control } = item;
        const SAS = this.SASControl.find((sas) => sas.Control == control) || null;
        if (!SAS) return item;
        const { Properties, PropertiesValue } = SAS;
        return {
          ...item,
          [Properties.toLowerCase()]: PropertiesValue == 'True',
        };
      });

      return menuItems;
    },

    prStatusUpdateSAS() {
      const branchCheck = Boolean(this.SASControl.find((item) => item.Control == 'mnuByTechnical'));
      const branchApprove = Boolean(this.SASControl.find((item) => item.Control == 'mnuBranch'));
      const hqApprove = Boolean(this.SASControl.find((item) => item.Control == 'mnuHQ'));

      return {
        branchCheck,
        branchApprove,
        hqApprove,
      };
    },

    prTableDataFiltered() {
      const relabel = this.prTableData.map((item, index) => {
        const {
          PrNo,
          PrDate,
          RequestBy,
          Position,
          Pesanan,
          Expenses,
          EstCost,
          Period,
          UserId,
          DateUpdate,
          BranchCheck,
          BranchApproval,
          HQApproval,
        } = item || {};
        return {
          '#': index + 1,
          'PR #': PrNo,
          Date: PrDate,
          'Requested By': `${RequestBy} ${Position ? `(${Position})` : ''}`,
          'PO/WO': Pesanan == '1' ? 'Work Order' : 'Purchase Order',
          'Expenses Type': Expenses == '1' ? 'OPEX' : 'CAPEX',
          'Estimated Cost': EstCost,
          Period: Period,
          'Approval From HQ': HQApproval == '1' ? 'Approved ✅' : 'Pending',
          'Approval From Branch': BranchApproval == '1' ? 'Approved ✅' : 'Pending',
          'Checking From Branch': BranchCheck == '1' ? 'Checked ✅' : 'Pending',
          'Updated By': UserId,
          'Date Updated': DateUpdate,
        };
      });
      return relabel;
    },

    lastUpdatedInfo() {
      if (!this.selectedPR) return '';

      const { UserId, DateUpdate } = this.selectedPR;
      return `${UserId} ${new Date(DateUpdate).formatString('yyyy-MM-dd HH:mm')}`;
    },

    popupTitle() {
      const PRNo = this.selectedPR?.PRNo ?? '';
      return `${this.menuMode} Purchase Requisition ${PRNo}`;
    },

    requestedByValue() {
      return `${this.requestedByUserIdValue} ${this.requestedByStaffPositionValue ? `(${this.requestedByStaffPositionValue})` : ''}`;
    },

    quotationsTitle() {
      return `Quotations (${this.prImagesTableDataFiltered?.length ?? '0'})`;
    },

    itemsTitle() {
      return `Items (${this.prItemsTableDataFiltered?.length ?? '0'})`;
    },
    tabItems() {
      return [
        { title: 'General Info' },
        { title: 'Description' },
        { title: this.quotationsTitle },
        { title: this.itemsTitle },
        { title: 'Person In Charge' },
      ];
    },

    prImagesTableDataFiltered() {
      return this.prImagesTableData
        .filter((item) => item.action != 'D')
        .map((item, index) => {
          const { ImageType, ImageName, PRImage, seq, action } = item;

          return {
            '#': index + 1,
            'Quotation Name': ImageName ?? '',
            'Quotation Type': ImageType ?? '',
            PRImage,
            seq,
            action,
          };
        });
    },

    prItemsTableDataFiltered() {
      return this.prItemsTableData
        .filter((item) => item.action != 'D')
        .map((item, index) => {
          const { Supplier, ItemTxnID, Qty, UnitPrice, SeqNo, GST2TaxCode, GST2TaxRate, Taxable_AmountGST2 } = item;

          const findSupplier = this.supplierList.find((item) => item['Supplier Code'] == Supplier);
          const findProduct = this.whProductList.find((item) => item['Product ID'] == ItemTxnID);

          const calcAmount = Number(Qty) * Number(UnitPrice);
          const calcNet = Number(calcAmount) + Number(Taxable_AmountGST2);
          return {
            '#': index + 1,
            Supplier: findSupplier?.label ?? '',
            Product: findProduct?.label ?? '',
            Qty: Qty ?? '',
            'Unit Price': UnitPrice ?? '',
            Amount: calcAmount ?? '',
            'Tax Code': GST2TaxCode ?? '',
            'Tax Rate(%)': GST2TaxRate ?? '',
            'Tax Amount': Taxable_AmountGST2 ?? '',
            'Amount With Tax': calcNet ?? '',
            SeqNo,
          };
        });
    },

    prEventTableDataFiltered() {
      return this.prEventTableData.map((item, index) => {
        const { StatusDescription, StaffNo, DateCreated } = item;
        return {
          '#': index + 1,
          'Status Description': StatusDescription ?? '',
          'User ID': StaffNo,
          'Date Updated': new Date(DateCreated).formatString('yyyy-MM-dd HH:mm'),
        };
      });
    },

    amountTotal() {
      const calcAmount = Number(this.quantityValue) * Number(this.unitPriceValue);
      return !isNaN(calcAmount) ? calcAmount : '0.00';
    },

    taxInfo() {
      if (!this.productValue) return null;

      const { APTaxType } = this.productValue;
      if (APTaxType == 'GST') return null;

      const { APTaxCode, APTaxDesc, APTaxRate } = this.productValue;

      const calcTaxAmount = Number(this.quantityValue) * Number(this.unitPriceValue) * (Number(APTaxRate) / 100);
      const showTaxAmount = !isNaN(calcTaxAmount) ? calcTaxAmount : '0.00';

      return {
        APTaxCode: APTaxCode,
        APTaxDesc: APTaxDesc,
        APTaxRate: APTaxRate,
        TaxAmount: showTaxAmount,
      };
    },

    netAmount() {
      let calcNet = this.amountTotal;
      if (this.taxInfo) {
        calcNet = Number(calcNet) + Number(this.taxInfo.TaxAmount);
      }
      return !isNaN(calcNet) ? calcNet : '0.00';
    },

    tableAdditionalButton() {
      if (this.viewMode) return [{ text: 'View', index: 0, width: 60 }];
      return [{ text: 'Edit', index: 0, width: 60 }];
    },
  },
  data: function () {
    return {
      prTableSelectedRows: [],
      prTableData: [],
      prTableLoading: false,

      prTableDataType: {
        '#': 'number',
        'PR #': 'string',
        Date: 'date',
        'Requested By': 'string',
        'PO/WO': 'string',
        'Expenses Type': 'string',
        'Estimated Cost': 'currency',
        Period: 'string',
        'Approval From HQ': 'string',
        'Checking From Branch': 'string',
        'Approval From Branch': 'string',
        'Updated By': 'string',
        'Updated Date': 'datetime',
      },

      showPopup: false,
      viewMode: false,
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryErrorMessages: [],

      selectedTabIndex: 0,

      pesananButtons: [
        { label: 'Purchase Order', keyValue: '0' },
        { label: 'Work Order', keyValue: '1' },
      ],
      expenseTypeButtons: [
        { label: 'CAPEX', keyValue: '0' },
        { label: 'OPEX', keyValue: '1' },
      ],

      selectedPR: null,

      prDateValue: null,
      requestedByUserIdValue: '',
      requestedByStaffPositionValue: '',
      pesananValue: null,
      expenseTypeValue: null,
      estimatedCostValue: '',
      periodUsageValue: '',

      prDateValueError: '',
      pesananValueError: '',
      expenseTypeValueError: '',

      titleValue: '',
      scopeValue: '',
      justificationValue: '',

      prImagesTableData: [],
      prImagesTableSelectedRows: [],

      prImagesDataType: {
        '#': 'number',
        'Quotation Name': 'string',
        'Quotation Type': 'string',
      },

      prItemsTableData: [],
      prItemsTableSelectedRows: [],

      prItemsDataType: {
        '#': 'string',
        Supplier: 'string',
        Product: 'string',
        Qty: 'number',
        'Unit Price': 'currency',
        Amount: 'currency',
        'Tax Code': 'string',
        'Tax Rate(%)': 'number',
        'Tax Amount': 'currency',
        'Amount With Tax': 'currency',
      },

      selectedPrItem: null,

      supplierValue: null,
      productValue: null,
      quantityValue: '',
      unitPriceValue: '',

      supplierValueError: '',
      productValueError: '',
      quantityValueError: '',
      unitPriceValueError: '',

      supplierDataType: {
        '#': 'number',
        'Supplier Code': 'string',
        'Short Name': 'string',
        'Supplier Name': 'string',
        Category: 'string',
        Address: 'string',
        PostCode: 'number',
        City: 'string',
        State: 'string',
        Country: 'string',
        Contact: 'string',
        'Tel No': 'string',
        'Fax No': 'string',
        BrCode: 'string',
        cocode: 'string',
        'Last Update Time': 'datetime',
        'Last Update UserID': 'string',
        'Create Time': 'string',
        'Create UserID': 'string',
      },

      whProductListDataType: {
        '#': 'number',
        'Product Code': 'string',
        Description: 'string',
        'Default Unit': 'string',
        'Item Type': 'string',
        'Product Origin': 'string',
        Size: 'string',
        Shade: 'string',
        Remarks: 'string',
      },

      prEventTableData: [],
      prEventDataType: {
        '#': 'number',
        'Status Code': 'string',
        'Status Description': 'string',
        'User ID': 'string',
        'User Name': 'string',
        'Date Updated': 'datetime',
      },

      supplierList: [],
      whProductList: [],

      pic1Value: '',
      email1Value: '',

      pic2Value: '',
      email2Value: '',

      pic3Value: '',
      email3Value: '',

      pic4Value: '',
      email4Value: '',

      pic5Value: '',
      email5Value: '',

      pic6Value: '',
      email6Value: '',

      employeeList: [],

      roleDetails: {},

      deleteRemarks: '',
      deleteRemarksError: '',
      showDeletePopup: false,

      SASControl: [],
    };
  },
  props: {},
  methods: {
    async menuClicked(e) {
      this.viewMode = false;
      this.menuMode = e.key;
      this.selectedTabIndex = 0;
      this.clearPopupError();
      this.clearPRDetails();
      switch (e.key) {
        case 'New': {
          this.showPopup = true;
          this.viewMode = false;
          const { LoginUserId } = this.$store.state.loginDetails;
          this.requestedByUserIdValue = LoginUserId;
          const { StaffPosition } = this.roleDetails || {};
          this.requestedByStaffPositionValue = StaffPosition ?? '';
          break;
        }
        case 'Edit': {
          const { 'PR #': PRNo } = this.prTableSelectedRows?.[0] || {};
          if (!PRNo) {
            this.$showAlert(
              'error',
              'No Purchase Requisition is selected',
              'Please select a Purchase Requisition to Edit',
            );
            break;
          }

          this.$fullscreenLoading('Getting Purchase Requisition Details...');
          await this.getPRDetails(PRNo);
          this.populatePRDetails();
          this.$fullscreenLoading();

          this.showPopup = true;
          break;
        }
        case 'Delete': {
          const { 'PR #': PRNo } = this.prTableSelectedRows?.[0] || {};
          if (!PRNo) {
            this.$showAlert(
              'error',
              'No Purchase Requisition is selected',
              'Please select a Purchase Requisition to Delete',
            );
            break;
          }
          this.deleteRemarks = '';
          this.showDeletePopup = true;
          break;
        }
        case 'View': {
          const { 'PR #': PRNo } = this.prTableSelectedRows?.[0] || {};
          if (!PRNo) {
            this.$showAlert(
              'error',
              'No Purchase Requisition is selected',
              'Please select a Purchase Requisition to View',
            );
            break;
          }

          this.$fullscreenLoading('Getting Purchase Requisition Details...');
          await this.getPRDetails(PRNo);
          this.populatePRDetails();
          this.$fullscreenLoading();

          this.viewMode = true;
          this.showPopup = true;
          break;
        }
        case 'Print': {
          const prNo = this.prTableSelectedRows?.[0]?.['PR #'];

          const print = this.$router.resolve(
            `/Home/${this.$route.params.user}/FinPayment/PrintPurchaseRequisition` + (prNo ? `?prno=${prNo}` : '')
          );

          window.open(print.href, '_blank');
          break;
        }
      }
    },

    async tabClick(_tabItem, tabIndex) {
      if (!this.viewMode && this.selectedPrItem) {
        const discard = await this.$showAlert(
          'warning',
          'Discard Changes',
          'You have unsaved changes.<br>Do you wish to discard the changes?',
          [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
        );

        if (discard == 'Cancel') return;
      }

      this.selectedTabIndex = tabIndex;
      this.clearPrItemFields();
    },

    async getPR() {
      this.prTableLoading = true;
      this.prTableData = [];

      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PURCHASEREQUISITION' });
      this.$nextTick(() => {
        this.prTableData = result;
        this.prTableLoading = false;
      });
    },

    async getPRDetails(PRNo) {
      const result = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'PURCHASEREQUISITION',
        recordid: PRNo,
      });

      this.selectedPR = result.PR;
    },

    clearPRDetails() {
      this.selectedPR = null;
      this.prImagesTableData = [];
      this.prItemsTableData = [];
      this.prEventTableData = [];

      this.clearPrItemFields();

      this.prDateValue = null;
      this.requestedByUserIdValue = '';
      this.requestedByStaffPositionValue = '';
      this.pesananValue = null;
      this.expenseTypeValue = null;
      this.estimatedCostValue = '';
      this.periodUsageValue = '';
      this.titleValue = '';
      this.scopeValue = '';
      this.justificationValue = '';

      this.pic1Value = '';
      this.pic2Value = '';
      this.pic3Value = '';
      this.pic4Value = '';
      this.pic5Value = '';
      this.pic6Value = '';
      this.email1Value = '';
      this.email2Value = '';
      this.email3Value = '';
      this.email4Value = '';
      this.email5Value = '';
      this.email6Value = '';

      this.prDateValueError = '';
      this.pesananValueError = '';
      this.expenseTypeValueError = '';
    },

    populatePRDetails() {
      const {
        PRDate,
        RequestBy,
        Position,
        Pesanan,
        Expenses,
        EstCost,
        Period,
        Title,
        Scope,
        Justification,
        PRImage,
        PRProd,
        PREvent,
        PIC1,
        PIC2,
        PIC3,
        PIC4,
        PIC5,
        PIC6,
        Email1,
        Email2,
        Email3,
        Email4,
        Email5,
        Email6,
      } = this.selectedPR || {};

      this.prDateValue = new Date(PRDate);
      this.requestedByUserIdValue = RequestBy;
      this.requestedByStaffPositionValue = Position;
      this.pesananValue = this.pesananButtons.find((btn) => btn.keyValue == Pesanan);
      this.expenseTypeValue = this.expenseTypeButtons.find((btn) => btn.keyValue == Expenses);
      this.estimatedCostValue = this.$currencyFormat(EstCost) ?? '';
      this.periodUsageValue = Period ?? '';

      this.titleValue = Title ?? '';
      this.scopeValue = Scope ?? '';
      this.justificationValue = Justification ?? '';

      const prImageArray = this.convertToArray(PRImage);
      this.prImagesTableData = [];
      this.$nextTick(() => {
        this.prImagesTableData = prImageArray ?? [];
      });

      const prProdArray = this.convertToArray(PRProd);
      this.prItemsTableData = [];
      this.$nextTick(() => {
        this.prItemsTableData = prProdArray ?? [];
      });

      this.pic1Value = PIC1 ?? '';
      this.pic2Value = PIC2 ?? '';
      this.pic3Value = PIC3 ?? '';
      this.pic4Value = PIC4 ?? '';
      this.pic5Value = PIC5 ?? '';
      this.pic6Value = PIC6 ?? '';

      this.email1Value = Email1 ?? '';
      this.email2Value = Email2 ?? '';
      this.email3Value = Email3 ?? '';
      this.email4Value = Email4 ?? '';
      this.email5Value = Email5 ?? '';
      this.email6Value = Email6 ?? '';

      const prEventArray = this.convertToArray(PREvent);
      this.prEventTableData = [];
      this.$nextTick(() => {
        this.prEventTableData = prEventArray ?? [];
      });
    },

    prImageDownload(_text, rowClicked) {
      const { PRImage, 'Quotation Name': ImgName } = rowClicked;
      if (!PRImage) {
        this.$showAlert(
          'error',
          'Quotation Unavailable For Download',
          'Quotation is corrupted or failed to save to database.',
        );
      }
      this.downloadBase64(PRImage, ImgName);
    },

    prImageAdd() {
      const input = this.$refs.fileInput;
      if (input) {
        input.value = ''; // reset previous selection
        input.click();
      }
    },

    onFileChange(event) {
      const files = event.target.files;
      if (!files || files.length == 0) return;

      if (files.length > 1) {
        alert('Please select only ONE file.');
        event.target.value = '';
        return;
      }

      const file = files[0];
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64Content = e.target.result.split(',')[1];
        const newSeq = (Math.max(...this.prImagesTableData.map((i) => Number(i.seq)), 0) + 1).toString();

        const newImage = {
          seq: newSeq,
          PRImage: base64Content,
          ImageType: file.name.substring(file.name.lastIndexOf('.')),
          ImagePath: file.name,
          ImageName: file.name,
          action: 'N',
        };

        let prImageData = this.prImagesTableData;
        prImageData = [...prImageData, newImage];

        this.prImagesTableData = [];
        this.$nextTick(() => (this.prImagesTableData = prImageData));
      };

      reader.readAsDataURL(file);
    },

    async prImageDelete() {
      const discard = await this.$showAlert(
        'warning',
        'Delete Quotation',
        'Are you sure you want to delete this quotation?',
        [{ text: 'Delete', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
      if (discard == 'Cancel') return false;

      let updatePrImage = this.prImagesTableData;
      const { seq, action } = this.prImagesTableSelectedRows?.[0] || {};

      if (action == 'N') {
        updatePrImage = updatePrImage.filter((i) => i.seq != seq);
      } else {
        updatePrImage = updatePrImage.map((i) => {
          if (i.seq == seq) i.action = 'D';
          return i;
        });
      }
      this.prImagesTableData = [];
      this.$nextTick(() => (this.prImagesTableData = updatePrImage));
    },

    prItemAddClick() {
      this.clearPrItemFields();
      this.selectedPrItem = {};
    },

    prItemCancel() {
      this.selectedPrItem = null;
    },

    prItemTableButtonClick(_buttonText, rowClicked) {
      this.clearPrItemFields();
      this.selectedPrItem = rowClicked;
      this.prItemsTableSelectedRows = [rowClicked];
      this.populatePrItem(rowClicked);
    },

    clearPrItemFields() {
      this.selectedPrItem = null;

      this.supplierValue = null;
      this.productValue = null;
      this.quantityValue = '';
      this.unitPriceValue = '';

      this.supplierValueError = '';
      this.productValueError = '';
      this.quantityValueError = '';
      this.unitPriceValueError = '';
    },

    populatePrItem(selected) {
      const findSelected = this.prItemsTableData.find((item) => item.SeqNo == selected.SeqNo);
      const { Supplier, ItemTxnID, Qty, UnitPrice } = findSelected || {};
      this.supplierValue = this.supplierList.find((item) => item['Supplier Code'] == Supplier) || null;
      this.productValue = this.whProductList.find((item) => item['Product ID'] == ItemTxnID) || null;
      this.quantityValue = Qty ?? '0';
      this.unitPriceValue = UnitPrice ?? '0';
    },

    whProductValueChanged(selected) {
      if (!selected) return;
      const { CostPrice } = selected;
      this.unitPriceValue = CostPrice?.toString() ?? '0';
    },

    prItemSave() {
      let error = false;

      if (!this.supplierValue) {
        this.supplierValueError = 'Supplier is required!';
        error = true;
      }

      if (!this.productValue) {
        this.productValueError = 'Product is required!';
        error = true;
      }

      if (!this.quantityValue) {
        this.quantityValueError = 'Quantity is required!';
        error = true;
      }

      if (!this.unitPriceValue) {
        this.unitPriceValueError = 'Unit Price is required!';
        error = true;
      }

      if (error) return;

      let prItems = this.prItemsTableData;
      const { SeqNo } = this.selectedPrItem;

      const { 'Supplier Code': SupplierCode } = this.supplierValue;
      const { 'Product ID': ItemTxnID, 'Product Code': ProductCode, APTaxCode, APTaxRate } = this.productValue;

      const calcTaxAmount = this.amountTotal * (Number(APTaxRate) / 100);

      const addPrItem = {
        ItemTxnID: ItemTxnID,
        PRNo: this.selectedPR?.PrNo ?? '',
        Product: ProductCode,
        Qty: this.quantityValue,
        Supplier: SupplierCode,
        UnitPrice: this.unitPriceValue,
        GST2TaxCode: APTaxCode,
        GST2TaxRate: APTaxRate,
        Taxable_AmountGST2: calcTaxAmount,
      };

      if (SeqNo) {
        prItems = prItems.map((item) => {
          if (item.SeqNo == SeqNo) {
            const { action } = item;
            return { ...item, ...addPrItem, SeqNo, action: action ?? 'E' };
          }
          return item;
        });
      } else {
        prItems = [
          ...prItems,
          {
            ...addPrItem,
            SeqNo: Math.max(...prItems.filter((item) => item.aciton != 'D').map((i) => Number(i.SeqNo)), 0) + 1,
            action: 'N',
          },
        ];
      }

      this.prItemsTableData = [];
      this.$nextTick(() => {
        this.prItemsTableData = prItems;
      });

      this.selectedPrItem = null;
    },

    async prItemDelete() {
      const { SeqNo, action } = this.prItemsTableSelectedRows?.[0] || {};
      if (!SeqNo) {
        this.$showAlert('error', 'Error', 'Please select an item to delete.');
        return;
      }

      let prItemsUpdate = this.prItemsTableData;

      const discard = await this.$showAlert('warning', 'Delete Item', 'Are you sure you want to delete this item?', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;

      if (action == 'N') {
        prItemsUpdate = prItemsUpdate.filter((item) => item.SeqNo != SeqNo && item.action != 'D');
      } else {
        prItemsUpdate = prItemsUpdate.map((item) => {
          if (item.SeqNo == SeqNo) {
            return { ...item, action: 'D' };
          }
          return item;
        });
      }

      this.prItemsTableData = [];
      this.$nextTick(() => {
        this.prItemsTableData = prItemsUpdate;
      });
    },

    clearPopupError() {
      this.popupEntryErrorMessages = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryRequiredError = [];
    },

    async popupEntrySaveClicked() {
      this.clearPopupError();

      const error = [];

      if (!this.prDateValue) {
        this.prDateValueError = 'Date is required!';
        error.push({ key: 'PRDate', value: this.prDateValueError });
      }

      if (!this.pesananValue) {
        this.pesananValueError = 'Pesanan is required!';
        error.push({ key: 'pesanan', value: this.pesananValueError });
      }

      if (!this.expenseTypeValue) {
        this.expenseTypeValueError = 'Expense is required!';
        error.push({ key: 'expenseType', value: this.expenseTypeValueError });
      }

      this.popupEntryRequiredError = error;
      if (error.length > 0) return;

      const { PRNo: selectedPrNo } = this.selectedPR || {};

      const requestbody = {
        prno: selectedPrNo,
        prdate: new Date(this.prDateValue).formatString('yyyy-MM-dd') ?? null,
        pesanan: this.pesananValue.keyValue ?? '0',
        expenses: this.expenseTypeValue.keyValue ?? '0',
        title: this.titleValue ?? '',
        scope: this.scopeValue ?? '',
        justification: this.justificationValue ?? '',
        estcost: this.estimatedCostValue ?? '',
        period: this.periodUsageValue ?? '',
        requestby: this.requestedByUserIdValue,
        position: this.requestedByPositionValue ?? '',
        pic1: this.pic1Value ?? '',
        pic2: this.pic2Value ?? '',
        pic3: this.pic3Value ?? '',
        pic4: this.pic4Value ?? '',
        pic5: this.pic5Value ?? '',
        pic6: this.pic6Value ?? '',
        email1: this.email1Value ?? '',
        email2: this.email2Value ?? '',
        email3: this.email3Value ?? '',
        email4: this.email4Value ?? '',
        email5: this.email5Value ?? '',
        email6: this.email6Value ?? '',

        action: selectedPrNo ? 'E' : 'N',

        quotations: this.prImagesTableData
          .filter((item) => item.action)
          .map((item) => {
            const { PRImage, ImageType, ImageName, seq, action } = item;

            return {
              primage: PRImage ?? '',
              imagetype: ImageType ?? '',
              imagepath: ImageName ?? '',
              seq,
              action,
            };
          }),

        pritems: this.prItemsTableData
          .filter((item) => item.action)
          .map((item) => {
            const {
              SeqNo,
              ItemTxnID,
              Product,
              Supplier,
              Qty,
              UnitPrice,
              GST2TaxCode,
              GST2TaxRate,
              Taxable_AmountGST2,
              action,
            } = item;
            return {
              seqno: SeqNo,
              itemtxnid: ItemTxnID,
              product: Product,
              supplier: Supplier,
              qty: Qty,
              unitprice: UnitPrice,
              gst2taxcode: GST2TaxCode ?? '',
              gst2taxrate: GST2TaxRate ?? '0',
              taxableamount: Taxable_AmountGST2 ?? '0',
              action,
            };
          }),
      };

      this.$fullscreenLoading('Saving Purchase Requisition...');
      const result = await this.$apiCallV2('/PurchaseRequisition/SavePR', requestbody);
      const { Results, ErrMsg, PRNo } = result?.[0] || {};

      if (Results == '1') {
        this.popupEntryErrorMessages.push(ErrMsg);
        this.$fullscreenLoading();
        return;
      }

      this.menuMode = 'View';
      this.viewMode = true;
      this.popupEntrySuccessMessages.push(ErrMsg);

      await this.getPRDetails(PRNo);
      this.populatePRDetails();
      this.getPR();
      this.prTableSelectedRows = [];
      this.$fullscreenLoading();
    },

    async deletePopupButtonClicked(buttonText) {
      if (buttonText == 'Cancel') {
        this.showDeletePopup = false;
        return;
      }
      if (!this.deleteRemarks) {
        this.deleteRemarksError = 'Reason is required!';
        return;
      }

      this.$fullscreenLoading('Deleting Purchase Requisition...');

      const { 'PR #': PRNoSelected } = this.prTableSelectedRows?.[0] || {};
      const requestBody = {
        prno: PRNoSelected,
        remarks: this.deleteRemarks,
        action: 'C',
        prdate: null,
      };

      const result = await this.$apiCallV2('/PurchaseRequisition/SavePR', requestBody);
      const { Results, ErrMsg } = result?.[0] || {};
      if (Results == '0') {
        this.$showAlert('success', 'Purchase Requisition Deleted', ErrMsg, [{ text: 'OK' }]);
      } else {
        this.$showAlert('error', 'Purchase Requisition Could Not Be Deleted', ErrMsg, [{ text: 'OK' }]);
      }

      this.showDeletePopup = false;
      await this.getPR();
      this.$fullscreenLoading();
    },

    async approvalClick(approvalType) {
      // BRC - Branch Checking
      // BR - Branch Approval
      // HQ - HQ Approval

      const textUpdates = {
        BRC: 'Checking From Branch',
        BR: 'Approval From Branch',
        HQ: 'Approval From HQ',
      };

      const discard = await this.$showAlert(
        'alert',
        `Are you sure you want to update this status (${textUpdates[approvalType]})?`,
        '',
        [{ text: 'Update', color: '#535cda', textColor: 'white' }, { text: 'Cancel' }],
      );
      if (discard == 'Cancel') return false;

      const { PRNo } = this.selectedPR || {};
      if (!PRNo) return;

      this.$fullscreenLoading('Updating Purchase Requisition Status...');

      const requestBody = {
        prno: PRNo,
        action: approvalType,
        prdate: null,
      };

      const result = await this.$apiCallV2('/PurchaseRequisition/SavePR', requestBody);
      const { Results, ErrMsg } = result?.[0] || {};
      if (Results == '0') {
        this.$showAlert('success', 'Purchase Requisition Status Updated', ErrMsg, [{ text: 'OK' }]);
      } else {
        this.$showAlert('error', 'Purchase Requisition Could Not Be Updated', ErrMsg, [{ text: 'OK' }]);
      }

      if (Results == '1') {
        this.popupEntryErrorMessages.push(ErrMsg);
        this.$fullscreenLoading();
        return;
      }

      this.menuMode = 'View';
      this.viewMode = true;
      this.popupEntrySuccessMessages.push(ErrMsg);

      await this.getPRDetails(PRNo);
      this.populatePRDetails();
      this.getPR();
      this.prTableSelectedRows = [];
      this.$fullscreenLoading();
    },

    //#region getLists
    async getSupplierList() {
      this.supplierList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SUPPLIER' }))?.map((item) => ({
        ...item,
        label: `${item['Supplier Code']} - ${item['Supplier Name']}`,
      }));
    },

    async getWhProduct() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WHPRODUCT' });
      this.whProductList = result.map((item) => ({
        ...item,
        label: `${item['Product Code']} - ${item['Description']}`,
      }));
    },

    async getEmployeeList() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'EMPLOYEE' });
      this.employeeList = result;
    },

    async GetRoleName() {
      const result = await this.$apiCallV2('/PurchaseRequisition/GetRoleName', {});
      this.roleDetails = result[0];
    },

    async getSAS() {
      this.SASControl = await this.$apiCallV2(
        `/SASControl/GetSASControlAccess?system=FINPAYMENT&page=Purchase Requisition`,
      );
    },
    //#endregion

    //#region HELPERS
    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    downloadBase64(base64Data, fileName) {
      // Remove data URI prefix if present
      if (base64Data.includes(',')) {
        base64Data = base64Data.split(',')[1];
      }

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: 'application/octet-stream',
      });

      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    //#endregion
  },
  async mounted() {
    this.$fullscreenLoading('Loading Purchase Requisition...');
    await Promise.all([
      this.getSupplierList(),
      this.getWhProduct(),
      this.getEmployeeList(),
      this.GetRoleName(),
      this.getPR(),
      this.getSAS(),
    ]);

    this.$fullscreenLoading();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.main {
  padding: 10px 45px;
  display: flex;
  flex-direction: row;
  font-size: 12px;

  > .left-menu-container {
    height: 100%;
    width: fit-content;
  }

  > .right-tablebox-container {
    height: 100%;
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
  }
}

.flex-container {
  display: flex;
  gap: 4px;
}
.flex-column {
  flex-direction: column;
}

.tab-component-container {
  margin-top: 5px;
  height: calc(100% - 30px); // deduct height of Inventory No and Inventory Code inputs

  > .tab-component {
    height: 100%;

    .tab-container {
      height: 100%;
    }
  }
}

.table-tab-container {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 10px;
  > *:only-child {
    grid-column: 1/-1;
  }

  .table-container {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }
}
</style>
